import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Settings } from "./components";
import { useProfile } from "../../hooks";
import { ClassLinkWelcomeModal, InviteFriendModal } from "../../modals";
import { getResumeStepIndex } from "../../modals/ClassLinkWelcomeModal/ClassLinkWelcomeModal";
import Envelope from "../../assets/InviteFriendEnvelope.png";
import classNames from "classnames";
import "./Profile.scss";
import { Outlet, useNavigate } from "react-router-dom";
import useWindowWidth from "../../hooks/useWindowWidth";
import StudentResources from "./components/StudentResources";
import { onBoardingGetInit, onBoardingUpdateInit } from "../../redux/actions";
import { authUser } from "../../redux/selectors/authSelectors";

const getOnboardingField = (data, ...keys) => {
  if (!data || typeof data !== "object") {
    return undefined;
  }

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(data, key) && data[key] !== undefined) {
      return data[key];
    }
  }

  return undefined;
};

const buildOnboardingPayload = (values = {}, { complete = false } = {}) => {
  const payload = {
    age:
      values.age === "" || values.age === undefined ? undefined : Number(values.age),
    grade: values.grade === "" || values.grade === undefined ? undefined : values.grade,
    careers: values.careers,
    certainty: values.certainty,
    education:
      values.education === "" || values.education === undefined
        ? undefined
        : Number(values.education),
    favorite_ice_cream_flavor: values.favorite_ice_cream_flavor,
    is_read_terms_and_policies: values.is_read_terms_and_policies,
    allow_data_sharing: values.allow_data_sharing,
  };
  if (complete) {
    payload.complete = true;
  }
  return payload;
};

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    user,
    pathname,
    settings,
    studentResourcesList,
    greatFinds,
    isInviteFriendModalopened,
    isLogOutModalOpened,
    isProfileChildRoute,
    isLoadingGreat,
    isLoadingPicks,
    handleSettingClick,
    handleLogOut,
    closeInviteFriendModal,
    openInviteFriendModal,
    openLogOutModal,
    closeLogoutModal,
    handlePagePicks,
    handlePageGreat,
  } = useProfile();
  const storedUser = useSelector(authUser);
  const {
    onboardingData,
    onboardingGetStatus,
    onboardingUpdateLoading,
    onboardingUpdateStatus,
    onboardingUpdateError,
  } = useSelector((state) => state.profileReducer);
  const { isMobile } = useWindowWidth();
  const [isClassLinkWelcomeOpen, setIsClassLinkWelcomeOpen] = useState(false);
  const [pendingSubmissionType, setPendingSubmissionType] = useState(null);

  const role = user?.role || storedUser?.role;
  const isStudent = role === "Student";
  const classlinkSourcedId =
    user?.classlink_sourced_id || storedUser?.classlink_sourced_id;
  const isClassLinkStudent = Boolean(classlinkSourcedId);
  const needsClassLinkOnboarding =
    isStudent &&
    isClassLinkStudent &&
    onboardingGetStatus === "success" &&
    onboardingData?.onboarding_complete === false;

  useEffect(() => {
    if (!isStudent || !isClassLinkStudent) {
      return;
    }
    dispatch(onBoardingGetInit());
  }, [dispatch, isClassLinkStudent, isStudent]);

  useEffect(() => {
    if (!needsClassLinkOnboarding) {
      return;
    }
    setIsClassLinkWelcomeOpen(true);
  }, [needsClassLinkOnboarding]);

  const onboardingQuestions = onboardingData?.questions;
  const onboardingEducationChoices = onboardingData?.education_choices;
  const onboardingGradeChoices = onboardingData?.grade_choices;

  const onboardingInitialValues = useMemo(() => {
    if (!onboardingData) {
      return {};
    }
    const gradeFromApi = getOnboardingField(onboardingData, "grade");
    const gradeForForm =
      gradeFromApi === "OT" && onboardingData.onboarding_complete === false
        ? ""
        : gradeFromApi ?? "";
    return {
      age: getOnboardingField(onboardingData, "age"),
      grade: gradeForForm,
      careers: getOnboardingField(onboardingData, "careers"),
      certainty: getOnboardingField(onboardingData, "certainty"),
      education: getOnboardingField(onboardingData, "education"),
      favorite_ice_cream_flavor: getOnboardingField(onboardingData, "favorite_ice_cream_flavor"),
      is_read_terms_and_policies: getOnboardingField(onboardingData, "is_read_terms_and_policies"),
      allow_data_sharing: getOnboardingField(onboardingData, "allow_data_sharing"),
    };
  }, [onboardingData]);

  const onboardingInitialStep = useMemo(
    () => getResumeStepIndex(onboardingQuestions, onboardingInitialValues),
    [onboardingInitialValues, onboardingQuestions]
  );

  useEffect(() => {
    if (onboardingUpdateStatus !== "success" || !pendingSubmissionType) {
      return;
    }

    setPendingSubmissionType(null);
    if (pendingSubmissionType === "complete") {
      setIsClassLinkWelcomeOpen(false);
      navigate("/profile/recommendations", { replace: true });
      return;
    }
  }, [navigate, onboardingUpdateStatus, pendingSubmissionType]);

  const submitOnboarding = (formValues, submissionType, complete) => {
    setPendingSubmissionType(submissionType);
    dispatch(onBoardingUpdateInit(buildOnboardingPayload(formValues, { complete })));
  };

  const handleOnboardingComplete = (formValues) => {
    submitOnboarding(formValues, "complete", true);
  };

  const handleOnboardingExplore = () => {
    navigate("/profile/recommendations", { replace: true });
  };

  return (
    <>
      <div
        className={classNames("profileCont", {
          bigHeight: isMobile && pathname !== "/profile",
        })}
      >
        <div className="profileCont__grid">
          <InviteFriendModal
            isOpened={isInviteFriendModalopened}
            img={Envelope}
            title="Help our Mission: Invite a Friend"
            text=""
            leftBtnText="Cancel"
            rightBtnText="Send"
            closeModal={closeInviteFriendModal}
          />
          <div
            className={classNames("profileCont__settingsCont", {
              hide: isMobile && isProfileChildRoute,
            })}
          >
            <Settings
              settings={settings}
              handleSettingClick={handleSettingClick}
              name={user?.first_name}
              aiReportUrl={user?.ai_report}
              handleLogOut={handleLogOut}
              surname={user?.last_name}
              openInviteFriendModal={openInviteFriendModal}
              openLogOutModal={openLogOutModal}
              closeLogoutModal={closeLogoutModal}
              isLogOutModalOpened={isLogOutModalOpened}
              isMobile={isMobile}
            />
          </div>
          <div
            className={classNames("profileCont__contentCont", {
              hide: isMobile && !isProfileChildRoute,
            })}
          >
            <Outlet
              context={{
                user,
              }}
            />
          </div>
        </div>
        <StudentResources isLoading={isLoadingPicks} list={studentResourcesList} title='Picks For You' handlePage={handlePagePicks} />
        <StudentResources isLoading={isLoadingGreat} list={greatFinds} title='Great Finds' handlePage={handlePageGreat} />
      </div>
      {isClassLinkStudent ? (
        <ClassLinkWelcomeModal
          educationChoices={onboardingEducationChoices}
          gradeChoices={onboardingGradeChoices}
          initialStep={onboardingInitialStep}
          initialValues={onboardingInitialValues}
          isOpen={isClassLinkWelcomeOpen}
          isSubmitting={onboardingUpdateLoading}
          onComplete={handleOnboardingComplete}
          onExplore={handleOnboardingExplore}
          questions={onboardingQuestions}
          submitError={onboardingUpdateError}
          userName={user?.first_name || storedUser?.first_name || "there"}
        />
      ) : null}
    </>
  );
};

export default Profile;
